import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useScanCapture } from "../../lib/scan/useScanCapture";
import { CameraCapture } from "./CameraCapture";
import { ScanCaptureSteps } from "./ScanCaptureSteps";
import { VoiceRecorder, type Recording } from "./VoiceRecorder";

export interface ScannedPhoto {
  /** What the user picked: the enhanced or the unenhanced crop. */
  file: File;
  /** The unenhanced crop when the enhanced one was picked, kept so the
   *  enhancement can be undone later (saved as original_media_id). */
  original: File | null;
  /** A spoken story about the photo, saved as its voice note. */
  voiceNote: Recording | null;
}

interface Props {
  acceptLabel: string;
  onAccept: (photo: ScannedPhoto) => void;
  onCancel: () => void;
}

/**
 * Full-screen scanner over another screen (the new-post form): camera →
 * corners → straighten and enhance → an optional spoken story, then hands
 * the result back. Rendered
 * into <body> so the page's stacking contexts (the post card, the fixed
 * header) can't sit on top of it.
 */
export function ScanOverlay({ acceptLabel, onAccept, onCancel }: Props) {
  const scan = useScanCapture();
  const [telling, setTelling] = useState(false);
  const [voice, setVoice] = useState<Recording | null>(null);

  // The page underneath shouldn't scroll while the overlay is up.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  if (scan.step === "camera") {
    return createPortal(<CameraCapture onCapture={(c) => void scan.onCapture(c)} onClose={onCancel} />, document.body);
  }

  function accept() {
    if (!scan.result || !scan.shown) return;
    const name = `scan-${Date.now()}.jpg`;
    onAccept({
      file: new File([scan.shown], name, { type: "image/jpeg" }),
      original: scan.useEnhanced ? new File([scan.result.original], name, { type: "image/jpeg" }) : null,
      voiceNote: voice,
    });
  }

  return createPortal(
    <div className="scan-overlay" role="dialog" aria-modal="true" aria-label="Scan an old photo">
      <div className="scan-overlay__inner stack-lg">
        <div className="row-between">
          <h2 className="scan-h2" style={{ margin: 0 }}>Scan an old photo</h2>
          <button type="button" className="btn-tertiary" onClick={onCancel}>
            Cancel
          </button>
        </div>
        {telling && scan.shownUrl ? (
          <section className="stack">
            <img className="scan-preview scan-preview--small" src={scan.shownUrl} alt="Scanned photo" />
            <span className="scan-h2">Tell the story</span>
            <p className="text-subtle" style={{ margin: 0 }}>
              Who is in it, where was it, what was happening? Speaking is often easier than typing. Up to 2 minutes,
              and it's written down for you.
            </p>
            <VoiceRecorder value={voice} onChange={setVoice} />
            <div className="scan-actions">
              <button type="button" className="btn-tertiary" onClick={() => setTelling(false)}>
                ← Photo
              </button>
              <button type="button" onClick={accept}>
                {voice ? acceptLabel : `Skip and ${acceptLabel.toLowerCase()}`}
              </button>
            </div>
          </section>
        ) : (
          <ScanCaptureSteps scan={scan} acceptLabel="Next" onAccept={() => setTelling(true)} />
        )}
        {scan.error && <p className="text-error">{scan.error}</p>}
      </div>
    </div>,
    document.body,
  );
}
